import { useState, useEffect, useMemo, useRef, ChangeEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import RefreshTwoToneIcon from '@mui/icons-material/RefreshTwoTone';
import { useTranslation } from 'react-i18next';
import { ticketService } from '@/data/ticketService';
import { ChildActivity } from '@/types/ChildActivity.interface';
import { Ticket } from '@/types/Ticket.interface';
import { TicketUsageStatus } from '@/enums/ticket-usage-status';
import { TicketStatusLabel } from '@/components/StatusLabel';
import StatusFilter from '@/components/filters/StatusFilter';
import NoDataFound from '@/components/NoDataFound';
import { formatDateToDayMonthYearTime } from '@/utils/dateFormatters';
import { formatCurrency } from '@/utils/formatters';
import { debounce, preparePurchaseTypeLabel } from '@/utils/helpers';

interface ChildActivityParticipantsDialogProps {
  open: boolean;
  onClose: () => void;
  childActivity: ChildActivity | null;
  // Seansın bağlı olduğu şube. Admin görünümünde seçili şube olmadığı için
  // currentBranch yerine etkinliğin kendi şubesi gönderilir.
  branchId?: number;
}

const ROWS_PER_PAGE_OPTIONS = [10, 30, 50, 100];
const COLUMN_COUNT = 7;

const ChildActivityParticipantsDialog = ({
  open,
  onClose,
  childActivity,
  branchId
}: ChildActivityParticipantsDialogProps) => {
  const { t } = useTranslation();

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  const [searchInput, setSearchInput] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [refreshKey, setRefreshKey] = useState<number>(0);
  // Yavaş dönen eski bir istek, yeni aramanın sonucunu ezmesin.
  const requestIdRef = useRef<number>(0);

  const childActivityId = childActivity?.id;

  const statusOptions = useMemo(
    () => [
      { id: 'all', name: t('all') },
      {
        id: TicketUsageStatus.NOT_USED,
        name: t('ticket.usage.status.not_used')
      },
      { id: TicketUsageStatus.USED, name: t('ticket.usage.status.used') },
      {
        id: TicketUsageStatus.REFUNDED,
        name: t('ticket.usage.status.refunded')
      },
      {
        id: TicketUsageStatus.CANCELED,
        name: t('ticket.usage.status.canceled')
      },
      { id: TicketUsageStatus.EXPIRED, name: t('ticket.usage.status.expired') }
    ],
    [t]
  );

  const applySearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value.trim());
        setPage(0);
      }, 500),
    []
  );

  useEffect(() => {
    if (!open) {
      setTickets([]);
      setTotal(0);
      setError(null);
      setPage(0);
      setSearchInput('');
      setSearch('');
      setStatus(undefined);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !childActivityId) return;

    const requestId = ++requestIdRef.current;

    const fetchParticipants = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await ticketService.getByChildActivity(childActivityId, {
          page,
          limit,
          ...(search && { search }),
          ...(status && { status }),
          ...(branchId && { branchId })
        });
        if (requestId !== requestIdRef.current) return;
        setTickets(result?.items || []);
        setTotal(result?.total || 0);
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        console.error('Error fetching session participants:', err);
        setTickets([]);
        setTotal(0);
        setError(t('child.activity.participants.load.error'));
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    };

    fetchParticipants();
  }, [
    open,
    childActivityId,
    branchId,
    page,
    limit,
    search,
    status,
    refreshKey,
    t
  ]);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
    applySearch(event.target.value);
  };

  const handleStatusChange = (value: string | undefined) => {
    setStatus(value);
    setPage(0);
  };

  const handleLimitChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLimit(Number.parseInt(event.target.value));
    setPage(0);
  };

  const isFiltered = Boolean(search || status);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography variant="h4" component="span">
          {t('child.activity.participants.title')}
        </Typography>
        {childActivity && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {childActivity.title}
            {' · '}
            {formatDateToDayMonthYearTime(childActivity.startDateTime)}
            {' - '}
            {formatDateToDayMonthYearTime(childActivity.endDateTime)}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          gap={2}
          px={3}
          py={2}
        >
          <OutlinedInput
            placeholder={t('child.activity.participants.search.placeholder')}
            value={searchInput}
            onChange={handleSearchChange}
            startAdornment={
              <InputAdornment position="start">
                <SearchOutlined />
              </InputAdornment>
            }
            size="small"
            sx={{ flex: '1 1 260px', maxWidth: 420 }}
            className="child-activity-participants-search"
          />

          <StatusFilter
            value={status}
            onChange={handleStatusChange}
            options={statusOptions}
            minWidth={170}
            size="small"
          />

          <Box flexGrow={1} />

          <Chip
            color="primary"
            variant="outlined"
            label={t(
              isFiltered
                ? 'child.activity.participants.count.filtered'
                : 'child.activity.participants.count',
              { total }
            )}
          />
          {childActivity?.capacity != null && (
            <Chip
              variant="outlined"
              label={`${t('capacity')}: ${childActivity.capacity}`}
            />
          )}
          <Tooltip arrow title={t('refresh.list')}>
            <IconButton
              color="primary"
              onClick={() => setRefreshKey((key) => key + 1)}
              disabled={loading}
            >
              <RefreshTwoToneIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {error && (
          <Box px={3} pb={2}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>{t('customer')}</TableCell>
                <TableCell>{t('phone')}</TableCell>
                <TableCell>{t('purchase.type')}</TableCell>
                <TableCell align="right">{t('price')}</TableCell>
                <TableCell align="center">{t('status')}</TableCell>
                <TableCell>
                  {t('child.activity.participants.purchased.at')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT}>
                    <Box p={2} display="flex" justifyContent="center">
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : tickets.length > 0 ? (
                tickets.map((ticket, index) => (
                  <TableRow hover key={ticket.id}>
                    <TableCell>{page * limit + index + 1}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body1"
                        fontWeight="bold"
                        color="text.primary"
                        noWrap
                      >
                        {[ticket.user?.name, ticket.user?.surname]
                          .filter(Boolean)
                          .join(' ') || '-'}
                      </Typography>
                      {ticket.user?.email && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                        >
                          {ticket.user.email}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {ticket.user?.phone || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {t(preparePurchaseTypeLabel(ticket.purchaseType))}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" noWrap>
                        {ticket.price != null
                          ? formatCurrency(ticket.price)
                          : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <TicketStatusLabel status={ticket.ticketUsageStatus} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {formatDateToDayMonthYearTime(ticket.createdAt)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <NoDataFound
                  message={t(
                    isFiltered
                      ? 'no.results.found'
                      : 'child.activity.participants.empty'
                  )}
                  colSpan={COLUMN_COUNT}
                />
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box px={2}>
          <TablePagination
            component="div"
            count={total}
            onPageChange={(_event, newPage) => setPage(newPage)}
            onRowsPerPageChange={handleLimitChange}
            page={total ? page : 0}
            rowsPerPage={limit}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChildActivityParticipantsDialog;
